import React, { useEffect, useState, useCallback } from 'react';
import { PeriodicReportingType } from '../PeriodicReporting';
import { getIndirectCostsByProjectIdAndStatementId, IndirectCostType } from 'lib/api_ts/periodicReportingApi';
import { useLocation } from 'react-router-dom-v5-compat';
import { Row } from 'react-bootstrap';
import { formatToDecimals } from 'lib/helpers/formatting';
import { Loading } from '@components/atomic/atoms';

interface PeriodicReportingIndirectCostsProps {
	data: PeriodicReportingType;
	projectIndirectCostsPerc: number;
}

const PeriodicReportingIndirectCosts: React.FC<PeriodicReportingIndirectCostsProps> = ({ data, projectIndirectCostsPerc }) => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	const [isLoading, setIsLoading] = useState(true);
	const [indirectCosts, setIndirectCosts] = useState<
		{
			owner: string;
			ownerId: number;
			costs: {
				noticeExpenseItemId: number;
				noticeExpenseItemName: string;
				noticeIndirectCosts: {
					computedAmount: number;
				};
				expenseItems: {
					id: number;
					name: string;
					amount: number;
					percentage: number;
					computedAmount: number;
					position: string;
				}[];
			}[];
			indirectCosts: {
				name: string;
				computedAmount: number;
				percentage: number;
				amount: number;
			};
		}[]
	>();

	const transformData = useCallback(
		(data: { [ownerId: number]: { items: IndirectCostType[]; indirectCosts: number; totalCosts: number } }) => {
			const result = [];

			for (const companyOwnerId in data) {
				const companyObject = data[companyOwnerId];

				if (companyObject.items.length === 0) continue;

				const ownerName = companyObject.items[0].companyOwner?.name;

				const groupedCosts = {};

				companyObject.items.forEach((item) => {
					const noticeExpenseItemId = item.expenseItem?.projectNoticeExpenseItemId;
					const noticeExpenseItemName = item.expenseItem?.noticeExpenseItem?.name;
					const position = item.expenseItem?.noticeExpenseItem?.position;

					if (!noticeExpenseItemId) return;

					if (!groupedCosts[noticeExpenseItemId]) {
						groupedCosts[noticeExpenseItemId] = {
							noticeExpenseItemId,
							noticeExpenseItemName,
							position,
							expenseItems: [],
						};
					}

					groupedCosts[noticeExpenseItemId].expenseItems.push({
						id: item.expenseItem.id,
						name: item.expenseItem.name,
						amount: item.amount,
						percentage: item.geShare,
						computedAmount: item.geAmount,
						position: item.expenseItem.position,
					});
				});

				result.push({
					owner: ownerName,
					ownerId: Number(companyOwnerId),
					costs: Object.values(groupedCosts),
					indirectCosts: {
						name: 'Internal Costs',
						computedAmount: companyObject.indirectCosts,
						amount: companyObject.totalCosts,
						percentage: projectIndirectCostsPerc,
					},
				});
			}

			return result;
		},
		[projectIndirectCostsPerc],
	);

	useEffect(() => {
		try {
			(async () => {
				const indirectCostsListFromApi = await getIndirectCostsByProjectIdAndStatementId(projectId, data.id);

				setIndirectCosts(transformData(indirectCostsListFromApi.statementGe));
				setIsLoading(false);
			})();
		} catch (error) {
			console.error('error', error);
		}
	}, [projectId, data.id, transformData]);

	const headers = [
		{ key: 'amount', label: 'Importo €' },
		{ key: 'percentage', label: 'Percentuale forfettaria %' },
		{ key: 'computedAmount', label: 'Importo costi indiretti €' },
	];

	return (
		<>
			{isLoading ? (
				<Loading fixedHeight="50vh" />
			) : indirectCosts.length > 0 ? (
				indirectCosts.map((indirectCost) => {
					return (
						<div key={indirectCost.owner + '-' + indirectCost.ownerId} style={{ paddingBlock: '1.5rem', paddingInline: '3rem' }}>
							<div className="mb-4 f-700 d-grid" style={{ gridTemplateColumns: 'minmax(auto, 30rem) repeat(3, 1fr)' }}>
								<div>{indirectCost.owner}</div>

								{headers.map((header) => (
									<div key={indirectCost.owner + '-' + header.key} className="text-end">
										{header.label}
									</div>
								))}
							</div>

							{indirectCost.costs.map((cost) => {
								return (
									<div key={indirectCost.ownerId + '-' + cost.noticeExpenseItemId} className="mb-3">
										<div className="mt-1 mb-3 ps-3 f-500">{cost.noticeExpenseItemName}</div>

										{cost.expenseItems.map((expenseItem) => {
											return (
												<div
													key={indirectCost.ownerId + '-' + expenseItem.id}
													className="mb-2 d-grid"
													style={{ gridTemplateColumns: 'minmax(auto, 30rem) repeat(3, 1fr)' }}
												>
													<div className="ps-4">{expenseItem.name}</div>
													{headers.map((header) => (
														<div key={indirectCost.ownerId + '-' + expenseItem.id + '-' + header.key} className="mb-2 text-end">
															{header.key === 'percentage'
																? formatToDecimals(expenseItem[header.key as keyof typeof expenseItem], 0, 2)
																: formatToDecimals(expenseItem[header.key as keyof typeof expenseItem])}
														</div>
													))}
												</div>
											);
										})}
									</div>
								);
							})}

							<div className="mb-3">
								<div className="mb-2 d-grid" style={{ gridTemplateColumns: 'minmax(auto, 30rem) repeat(3, 1fr)' }}>
									<div className="mt-1 mb-3 ps-3 f-500">{indirectCost.indirectCosts.name}</div>
									{headers.map((header, i) => (
										<div key={indirectCost.ownerId + '-' + header.key + '-' + i} className="mb-2 text-end">
											{header.key === 'percentage'
												? formatToDecimals(indirectCost.indirectCosts[header.key as keyof typeof indirectCosts], 0, 2)
												: formatToDecimals(indirectCost.indirectCosts[header.key as keyof typeof indirectCosts])}
										</div>
									))}
								</div>
							</div>
						</div>
					);
				})
			) : (
				<Row className="text-center my-5">
					<span>Non sono presenti costi indiretti per questo rendiconto</span>
				</Row>
			)}
		</>
	);
};

export default PeriodicReportingIndirectCosts;
